import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, QrCode, Star, Moon, ScrollText, Globe2, ArrowRight } from "lucide-react";
import { api, money } from "../lib/api";

const HERO = "https://images.unsplash.com/photo-1534796636912-3b95b3ab5986?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHw0fHxzdGFycnklMjBjb25zdGVsbGF0aW9uJTIwbmlnaHQlMjBza3klMjBkZWVwJTIwc3BhY2V8ZW58MHx8fHwxNzg3NjY5NTcxfDA&ixlib=rb-4.1.0&q=85";
const MOON_IMG = "https://images.pexels.com/photos/38671268/pexels-photo-38671268.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const STAR_IMG = "https://images.unsplash.com/photo-1628498188873-579210ce622e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwzfHxzdGFycnklMjBjb25zdGVsbGF0aW9uJTIwbmlnaHQlMjBza3klMjBkZWVwJTIwc3BhY2V8ZW58MHx8fHwxNzg3NjY5NTcxfDA&ixlib=rb-4.1.0&q=85";
const OBS_IMG = "https://images.unsplash.com/photo-1765303018701-ffe7a1464d37?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHw0fHx0ZWxlc2NvcGUlMjBzdGFyZ2F6aW5nJTIwbmlnaHQlMjBza3klMjBzdGFycyUyMGNvc21vc3xlbnwwfHx8fDE3ODc2Njk1NzF8MA&ixlib=rb-4.1.0&q=85";

export default function Home() {
  const [pricing, setPricing] = useState(null);

  useEffect(() => {
    api.get("/pricing").then(({ data }) => setPricing(data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <section className="relative overflow-hidden">
        <img src={HERO} alt="Deep space starfield" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(5,8,17,.55) 0%, rgba(5,8,17,.85) 60%, #050811 100%)" }} />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-10 pt-28 pb-36">
          <div className="max-w-3xl hy-rise">
            <div className="hy-label">The Transparent Celestial Registry</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mt-6">
              Commemorate a moment<br />
              <span className="hy-gold-text">among the stars</span>
            </h1>
            <p className="font-serif-accent italic text-lg md:text-xl text-slate-300 mt-8 max-w-2xl leading-relaxed">
              Moon • Stars • Memories • Verified — a permanent, publicly verifiable commemorative record
              with a numbered registry entry, celestial coordinates, certificate and QR verification.
            </p>
            <div className="flex flex-wrap gap-4 mt-12">
              <Link to="/create" data-testid="hero-create-btn" className="hy-btn px-9 py-4 text-xs uppercase flex items-center gap-2">
                Register a Dedication <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/transparency" data-testid="hero-transparency-btn" className="hy-btn-ghost px-9 py-4 text-xs uppercase tracking-wider">
                What You Actually Receive
              </Link>
            </div>
            {pricing && (
              <div className="mt-16 flex flex-wrap gap-10">
                <Stat label="Next Registry Number" value={`#${String(pricing.next_registry_number).padStart(4, "0")}`} />
                <Stat label="Next Edition" value={pricing.next_edition} />
                <Stat label="From" value={money(pricing.editions.find((e) => e.edition === "CELESTIAL")?.price)} />
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-10 -mt-16 relative z-10">
        <div className="hy-glass p-8 sm:p-12" data-testid="transparency-banner">
          <div className="hy-label mb-4">Transparency First</div>
          <p className="text-slate-300 text-sm sm:text-base leading-relaxed max-w-4xl">
            HYLUNER is a <span className="text-amber-200">private commemorative registry</span>. We do not sell
            lunar land, we do not sell stars, and we never claim legal ownership or government-recognised title
            over any celestial body. What you receive is a permanent, verifiable dedication record — and we state
            exactly that, everywhere.
          </p>
          <Link to="/transparency" className="inline-flex items-center gap-2 text-amber-300 text-sm mt-6 hover:gap-3 transition-all">
            Read the full transparency statement <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-10 py-28">
        <div className="hy-label">Two Registry Types</div>
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl text-amber-100 mt-4">One unified registry engine</h2>
        <div className="grid md:grid-cols-2 gap-8 mt-14">
          <TypeCard img={MOON_IMG} icon={Moon} title="Moon Parcels"
            desc="A symbolic lunar parcel referenced by selenographic coordinates in the Mean Earth / Polar Axis frame, with a named commemorative record."
            to="/create?type=MOON" testid="type-card-moon" />
          <TypeCard img={STAR_IMG} icon={Star} title="Star Dedications"
            desc="A catalogued star position expressed in ICRS / J2000.0 right ascension and declination, dedicated to a person, memory or moment."
            to="/create?type=STAR" testid="type-card-star" />
        </div>
      </section>

      <section className="border-y border-amber-500/15 bg-[#0b0f19]/60">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-24 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="hy-label">Verification</div>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl text-amber-100 mt-4">Every record is checkable</h2>
            <div className="space-y-8 mt-10">
              <Feature icon={ScrollText} title="Numbered registry entry"
                desc="One continuous HYLUNER numbering system. Your number is permanent and never reissued." />
              <Feature icon={QrCode} title="QR verification"
                desc="Each certificate carries a QR code that resolves to a live public verification page." />
              <Feature icon={Globe2} title="Public registry page"
                desc="Paid records appear in the public registry wall with their coordinates and dedication." />
              <Feature icon={ShieldCheck} title="Honest disclosure"
                desc="Every certificate prints exactly what the record does and does not represent." />
            </div>
          </div>
          <img src={OBS_IMG} alt="Observatory under night sky" className="w-full h-[440px] object-cover rounded-sm border border-amber-500/25" />
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-10 py-28">
        <div className="hy-label">Editions</div>
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl text-amber-100 mt-4">A permanent place in the sequence</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
          {(pricing?.editions || []).map((e) => (
            <div key={e.edition} className="hy-card p-7" data-testid={`edition-preview-${e.edition}`}>
              <div className="hy-label">{e.edition}</div>
              <div className="font-mono-hy text-xs text-slate-500 mt-3">
                #{String(e.range_start).padStart(3, "0")}–{e.range_end ? String(e.range_end).padStart(3, "0") : "∞"}
              </div>
              <div className="font-display text-2xl text-white mt-5">{money(e.price)}</div>
              <div className="text-xs text-slate-500 mt-3">
                {e.remaining === null ? "Open series" : `${e.remaining} of ${e.total} remaining`}
              </div>
            </div>
          ))}
        </div>
        <Link to="/editions" className="hy-btn inline-block px-9 py-4 text-xs uppercase mt-12">Compare Editions</Link>
      </section>
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div>
    <div className="hy-label">{label}</div>
    <div className="font-display text-2xl text-amber-200 mt-2">{value}</div>
  </div>
);

const TypeCard = ({ img, icon: Icon, title, desc, to, testid }) => (
  <Link to={to} className="hy-card block overflow-hidden group" data-testid={testid}>
    <img src={img} alt={title} className="w-full h-60 object-cover opacity-75 group-hover:opacity-100 transition-opacity duration-500" />
    <div className="p-8">
      <Icon className="w-6 h-6" style={{ color: "var(--hy-gold)" }} strokeWidth={1.4} />
      <h3 className="font-display text-xl sm:text-2xl text-amber-200/90 mt-5">{title}</h3>
      <p className="text-sm text-slate-400 mt-4 leading-relaxed">{desc}</p>
      <span className="inline-flex items-center gap-2 text-amber-300 text-xs uppercase tracking-widest mt-6">
        Begin <ArrowRight className="w-3 h-3" />
      </span>
    </div>
  </Link>
);

const Feature = ({ icon: Icon, title, desc }) => (
  <div className="flex gap-5">
    <Icon className="w-5 h-5 mt-1 shrink-0" style={{ color: "var(--hy-gold)" }} strokeWidth={1.5} />
    <div>
      <div className="text-white font-medium">{title}</div>
      <p className="text-sm text-slate-400 mt-1.5 leading-relaxed">{desc}</p>
    </div>
  </div>
);
